import React, { useMemo, useState, useEffect } from 'react';
import './DesktopStrategyPath.css';

const RING_COLORS = ['#A78BFA', '#60A5FA', '#5EEAD4', '#4ADE80', '#F472B6'];

function initialsFor(name) {
  return name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function kindClass(t) {
  if (t === 'Nothing owed') return 'clear';
  if (t === 'You do this') return 'owe';
  return 'person';
}

function formatMoney(n) {
  if (n == null || isNaN(n)) return null;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${n.toLocaleString()}`;
}

// Positions for a "many" cluster - golden-angle spiral packing, same geometry
// as the original prototype, scaled down to fit a stage card instead of a
// full viewport.
function clusterOffset(k) {
  const angle = k * 2.399963;
  const radius = 6 + 3.6 * Math.sqrt(k);
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius * 0.92 };
}

function StageVisual({ stage, colorFor }) {
  if (stage.kind === 'one') {
    const name = stage.donorNames[0];
    return (
      <div className="strategy-stage-visual">
        <div className="strategy-stage-lead" style={{ '--ring-color': colorFor(name) }}>
          <span>{initialsFor(name)}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="strategy-stage-visual">
      <div className="strategy-stage-cluster">
        {stage.donorNames.map((name, k) => {
          const { x, y } = clusterOffset(k);
          const size = Math.max(13, 24 - stage.donorNames.length * 0.5);
          return (
            <div
              key={name}
              className="strategy-stage-cluster-dot"
              title={name}
              style={{
                '--ring-color': colorFor(name),
                width: size, height: size,
                transform: `translate(${x}px, ${y}px)`
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function DesktopStrategyPath({ donors, strategies, goal, versionIndex, onChangeVersion, onBackToGrid }) {
  const strategy = strategies[versionIndex];
  const allNames = useMemo(() => Object.keys(donors || {}), [donors]);

  const colorFor = useMemo(() => {
    const map = {};
    allNames.forEach((name, i) => { map[name] = RING_COLORS[i % RING_COLORS.length]; });
    return (name) => map[name] || RING_COLORS[0];
  }, [allNames]);

  const usedNames = useMemo(() => {
    const set = new Set();
    (strategy?.stages || []).forEach(s => s.donorNames.forEach(n => set.add(n)));
    return set;
  }, [strategy]);

  const restNames = useMemo(() => allNames.filter(n => !usedNames.has(n)), [allNames, usedNames]);

  // Keyboard cycling, ignored while typing in the dock's textarea
  useEffect(() => {
    const handler = (e) => {
      if (document.activeElement?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') onChangeVersion((versionIndex - 1 + strategies.length) % strategies.length);
      if (e.key === 'ArrowRight') onChangeVersion((versionIndex + 1) % strategies.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [versionIndex, strategies.length, onChangeVersion]);

  if (!strategy) return null;
  const stages = strategy.stages;
  const n = stages.length;

  return (
    <div className="strategy-path">
      <div className="strategy-path-header">
        <div className="strategy-path-stat">
          <span className="strategy-path-stat-label">People</span>
          <span className="strategy-path-stat-value">{allNames.length}</span>
        </div>
        <div className="strategy-path-stat">
          <span className="strategy-path-stat-label">In This Path</span>
          <span className="strategy-path-stat-value">{usedNames.size}</span>
        </div>
        {formatMoney(goal) && (
          <div className="strategy-path-stat">
            <span className="strategy-path-stat-label">Target</span>
            <span className="strategy-path-stat-value">{formatMoney(goal)}</span>
          </div>
        )}
      </div>

      <div className="strategy-path-stages">
        {stages.map((stage, i) => (
          <div className="strategy-stage" key={i}>
            <p className="strategy-stage-pre">{stage.pre}</p>
            <StageVisual stage={stage} colorFor={colorFor} />
            <div className={`strategy-slab ${kindClass(stage.t)}`}>
              <p className="strategy-slab-type">{stage.t}</p>
              <p className="strategy-slab-name">
                {stage.kind === 'one' ? stage.donorNames[0] : `${stage.donorNames.length} ${strategy.name ? '' : 'people'}`.trim()}
              </p>
              {stage.totalLabel && <p className="strategy-slab-total">{stage.totalLabel}</p>}
            </div>
          </div>
        ))}

        {n > 1 && (
          <svg className="strategy-arcs" preserveAspectRatio="none" viewBox="0 0 1000 100">
            <defs>
              <linearGradient id="strategyArcGrad" x1="0" x2="1">
                <stop offset="0" stopColor="#A78BFA" stopOpacity="0.65" />
                <stop offset="1" stopColor="#60A5FA" stopOpacity="0.65" />
              </linearGradient>
            </defs>
            {stages.slice(0, -1).map((_, i) => {
              const x1 = ((i + 0.5) / n) * 1000;
              const x2 = ((i + 1.5) / n) * 1000;
              const mx = (x1 + x2) / 2;
              return (
                <path
                  key={i}
                  d={`M${x1} 30 C${mx} 5, ${mx} 5, ${x2} 30`}
                  fill="none"
                  stroke="url(#strategyArcGrad)"
                  strokeWidth="1.4"
                />
              );
            })}
          </svg>
        )}
      </div>

      {restNames.length > 0 && (
        <div className="strategy-path-rest">
          {restNames.map(name => (
            <div key={name} className="strategy-rest-dot" style={{ '--ring-color': colorFor(name) }} title={name} />
          ))}
        </div>
      )}

      <div className="strategy-version-bar">
        <div className="strategy-version-info">
          <p className="strategy-version-meta">
            Version {versionIndex + 1} of {strategies.length} · ← → to cycle
          </p>
          <h3>{strategy.name}</h3>
          <p className="strategy-version-premise" dangerouslySetInnerHTML={{ __html: strategy.premise }} />
        </div>
        <div className="strategy-version-nav">
          <button className="strategy-vbtn" onClick={() => onChangeVersion((versionIndex - 1 + strategies.length) % strategies.length)} aria-label="Previous">‹</button>
          <div className="strategy-vdots">
            {strategies.map((_, i) => (
              <i key={i} className={i === versionIndex ? 'on' : ''} />
            ))}
          </div>
          <button className="strategy-vbtn" onClick={() => onChangeVersion((versionIndex + 1) % strategies.length)} aria-label="Next">›</button>
          <button className="strategy-vbtn strategy-vbtn-text" onClick={onBackToGrid}>grid</button>
        </div>
      </div>
    </div>
  );
}

export default DesktopStrategyPath;
